import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

// Global Components
import Header from './components/Header';
import Footer from './components/Footer';
import Preloader from './components/Preloader';
import BackgroundFX from './components/BackgroundFX';

// Pages (lazy-loaded per route to reduce initial JS work)
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Articles = lazy(() => import('./pages/Articles'));
const Contact = lazy(() => import('./pages/Contact'));
const FAQs = lazy(() => import('./pages/FAQs'));
const SingleArticle = lazy(() => import('./pages/SingleArticle'));
const SingleService = lazy(() => import('./pages/SingleService'));
const NotFound = lazy(() => import('./pages/404'));
const Career = lazy(() => import('./pages/Career'));
const CourseRegistration = lazy(() => import('./pages/CourseRegistration'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));

// Services Pages
const ServiceCustomSoftware = lazy(() => import('./pages/ServiceCustomSoftware'));
const ServiceCMS = lazy(() => import('./pages/ServiceCMS'));
const ServiceWorkflowAutomation = lazy(() => import('./pages/ServiceWorkflowAutomation'));
const ServiceGenerativeAI = lazy(() => import('./pages/ServiceGenerativeAI'));
const ServiceAISystems = lazy(() => import('./pages/ServiceAISystems'));
const ServiceSaaSEngineering = lazy(() => import('./pages/ServiceSaaSEngineering'));

// Case Studies (new, locked-down set)
const CaseStudiesPage = lazy(() => import('./features/caseStudies/CaseStudiesPage'));
const CaseStudyLMS = lazy(() =>
  import('./features/caseStudies/caseStudyPages').then((module) => ({
    default: module.CaseStudyLMS,
  })),
);

// Individual Articles
const AutomationWorkflowsArticle = lazy(() => import('./pages/articles/AutomationWorkflows'));
const DesigningAISystemsArticle = lazy(() => import('./pages/articles/DesigningAISystems'));
const GenerativeAIProductionArticle = lazy(() => import('./pages/articles/GenerativeAIProduction'));
const CMS2026Article = lazy(() => import('./pages/articles/CMS2026'));
const SaaSArchitectureArticle = lazy(() => import('./pages/articles/SaaSArchitecture'));
const SoftwareThatLastsArticle = lazy(() => import('./pages/articles/SoftwareThatLasts'));
const AIAutomationArticle = lazy(() => import('./pages/articles/AIAutomation'));
const EnterpriseAppDevArticle = lazy(() => import('./pages/articles/EnterpriseAppDev'));
const DataAsAProductArticle = lazy(() => import('./pages/articles/DataAsAProduct'));
const AIReadinessArticle = lazy(() => import('./pages/articles/AIReadiness'));
const APIFirstDevelopmentArticle = lazy(() => import('./pages/articles/APIFirstDevelopment'));
const SecurityByDesignArticle = lazy(() => import('./pages/articles/SecurityByDesign'));
const AutomationROIArticle = lazy(() => import('./pages/articles/AutomationROI'));
const ChoosingTechStackArticle = lazy(() => import('./pages/articles/ChoosingTechStack'));
const FutureOfITConsultingArticle = lazy(() => import('./pages/articles/FutureOfITConsulting'));
const PRELOADER_MIN_MS = 700;

function App() {
  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    return sessionStorage.getItem('nalexus-preloader-seen') !== 'true';
  });

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading(false);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('nalexus-preloader-seen', 'true');
      }
    }, PRELOADER_MIN_MS);

    return () => clearTimeout(timer);
  }, [loading]);

  if (loading) {
    return <Preloader />;
  }

  return (
    <Router>
      <BackgroundFX />
      <div className="app-shell">
        <Header />
        <AnimatedRoutes />
        <Footer />
      </div>
    </Router>
  );
}

export default App;

const AnimatedRoutes = () => {
  const location = useLocation();
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const pageInitial = reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 };
  const pageAnimate = { opacity: 1, y: 0 };
  const pageExit = reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -12 };
  const pageTransition = reduceMotion
    ? { duration: 0 }
    : { duration: 0.35, ease: [0.22, 0.61, 0.36, 1] };

  return (
    <AnimatePresence mode="wait">
      <motion.main
        key={location.pathname}
        className="page-transition"
        initial={pageInitial}
        animate={pageAnimate}
        exit={pageExit}
        transition={pageTransition}
      >
        <Suspense fallback={null}>
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/case-studies" element={<CaseStudiesPage />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/career" element={<Career />} />
            <Route path="/course-registration" element={<CourseRegistration />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />

            {/* Services Detail Pages */}
            <Route
              path="/services/custom-software-enterprise-applications"
              element={<ServiceCustomSoftware />}
            />
            <Route path="/services/cms-digital-experience-platforms" element={<ServiceCMS />} />
            <Route
              path="/services/workflow-automation-system-integrations"
              element={<ServiceWorkflowAutomation />}
            />
            <Route path="/services/generative-ai-solutions" element={<ServiceGenerativeAI />} />
            <Route path="/services/ai-systems-integrations" element={<ServiceAISystems />} />
            <Route path="/services/saas-product-engineering" element={<ServiceSaaSEngineering />} />

            {/* Article Detail Pages */}
            <Route path="/articles/automation-workflows-scalable-operations" element={<AutomationWorkflowsArticle />} />
            <Route path="/articles/designing-ai-systems-business-problems" element={<DesigningAISystemsArticle />} />
            <Route path="/articles/generative-ai-in-production" element={<GenerativeAIProductionArticle />} />
            <Route path="/articles/cms-2026-content-architecture" element={<CMS2026Article />} />
            <Route path="/articles/saas-architecture-explained" element={<SaaSArchitectureArticle />} />
            <Route path="/articles/building-software-that-lasts" element={<SoftwareThatLastsArticle />} />
            <Route path="/articles/ai-plus-automation" element={<AIAutomationArticle />} />
            <Route path="/articles/enterprise-application-development" element={<EnterpriseAppDevArticle />} />
            <Route path="/articles/data-as-a-product" element={<DataAsAProductArticle />} />
            <Route path="/articles/ai-readiness-assessment" element={<AIReadinessArticle />} />
            <Route path="/articles/api-first-development" element={<APIFirstDevelopmentArticle />} />
            <Route path="/articles/security-by-design" element={<SecurityByDesignArticle />} />
            <Route path="/articles/automation-roi" element={<AutomationROIArticle />} />
            <Route path="/articles/choosing-right-tech-stack" element={<ChoosingTechStackArticle />} />
            <Route path="/articles/future-of-it-consulting" element={<FutureOfITConsultingArticle />} />
            <Route path="/articles/:id" element={<SingleArticle />} />

            {/* Dynamic (/:id) Detail Pages */}
            <Route path="/article/:id" element={<SingleArticle />} />
            <Route path="/service/:id" element={<SingleService />} />

            {/* Case Studies */}
            <Route path="/case-studies/lms" element={<CaseStudyLMS />} />

            {/* 404 Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.main>
    </AnimatePresence>
  );
};
